import moment from "jalali-moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable, { Column } from "../../components/DataTable";
import endpoints from "../../config/endpoints";
import orderEnum from "../../enums/orderEnum";
import { Header } from "../../layouts/Header";
import { Sidebar } from "../../layouts/Sidebar";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import { useAppSelector } from "../../services/store";
import tools from "../../utils/tools";
import Status = orderEnum.Status;

const Orders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const homeReducer = useAppSelector((state) => state.homeReducer);
  const [data, setData] = useState<any[]>([]);
  const [tab, setTab] = useState("all");
  const [tableLoading, setTableLoading] = useState(false);

  const tabTitles: Record<string, string> = {
    all: "همه",
    setPrice: "در انتظار قیمت دهی",
    callPrice: "اعلام قیمت",
    clientApproved: "تایید برای خرید",
    accounting: "تایید حسابداری",
  };

  /* ── Actions ──────────────────────────────────────── */
  const deleteOrder = async (id: number) => {
    if (!confirm("آیا مطمئن هستید؟")) return;
    dispatch(setLoading(true));
    const res = await restApi(
      process.env.REACT_APP_BASE_URL + "/admin/order/" + id,
      true,
    ).delete({});
    if (res.code === 204) {
      Swal.fire({
        title: "موفق",
        text: "سفارش با موفقیت حذف شد",
        icon: "success",
        confirmButtonText: "متوجه شدم",
      });
      setData((prev) => prev.filter((o) => o.id !== id));
    } else {
      Swal.fire({
        title: "ناموفق",
        text: res?.data,
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    }
    dispatch(setLoading(false));
  };

  const handleStatusChange = async (id: number, status: Status) => {
    if (!confirm("آیا مطمئن هستید؟")) return;
    const res = await restApi(endpoints.order.status + id, true).put({
      status,
    });
    if (res.code === 200) {
      Swal.fire({
        title: "موفق",
        text: "وضعیت با موفقیت تغییر کرد",
        icon: "success",
        confirmButtonText: "متوجه شدم",
      });
    } else {
      Swal.fire({
        title: "ناموفق",
        text: res?.data,
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    }
    dispatch(setLoading(false));
  };

  /* ── Tab filter ───────────────────────────────────── */
  const tabFilter = (order: any) => {
    switch (tab) {
      case "accounting":
        return order.orderStatusSlug === Status.Accounting;
      case "setPrice":
        return order.orderStatusSlug === Status.SetPrice;
      case "callPrice":
        return order.orderStatusSlug === Status.CallPrice;
      case "clientApproved":
        return order.orderStatusSlug === Status.ClientApproved;
      default:
        return true;
    }
  };

  /* ── Columns ──────────────────────────────────────── */
  const columns: Column[] = [
    {
      key: "code",
      title: "شماره سفارش",
      sortable: true,
      searchable: true,
    },
    {
      key: "createdAt",
      title: "تاریخ",
      sortable: true,
      searchable: true,
      render: (val) => (val ? moment(val).format("jYYYY/jMM/jDD") : "—"),
    },
    {
      key: "user",
      title: "کاربر",
      sortable: true,
      searchable: true,
      // shown by last name; searchable by last name or mobile number
      render: (_, row) => row?.user?.lastName || row?.user?.name || "—",
      searchValue: (row) =>
        [row?.user?.lastName, row?.user?.phoneNumber].filter(Boolean).join(" "),
      sortValue: (row) => row?.user?.lastName || row?.user?.name || "",
    },
    {
      key: "orderStatusSlug",
      title: "وضعیت سفارش",
      sortable: true,
      searchable: false,
      render: (_, row) => (
        <select
          className="billInput"
          defaultValue={
            homeReducer.statuses?.find((s) => s.slug === row.orderStatusSlug)
              ?.id
          }
          onChange={(e) => {
            const status = homeReducer.statuses?.find(
              (s) => String(s.id) === e.target.value,
            )?.slug;
            if (status) handleStatusChange(row.id, status);
          }}
        >
          {homeReducer.statuses?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "price",
      title: "مبلغ کل",
      sortable: true,
      searchable: false,
      render: (val) => tools.engNumToPersian(tools.formatPrice(val)),
    },
    {
      key: "actions",
      title: "عملیات",
      sortable: false,
      searchable: false,
      width: "170px",
      render: (_, row) => (
        <div className="svgContainer">
          <i
            className="trash clickable"
            title="حذف"
            onClick={() => deleteOrder(row.id)}
          />
          <i
            className="edit clickable"
            title="ویرایش"
            onClick={() => navigate("/order/edit/" + row.id)}
          />
          <i
            className="labelSvg clickable"
            title="چاپ برچسب"
            onClick={() => window.open("/order/label/" + row.id, "_blank")}
          />
          <i
            className="printSvg clickable"
            title="چاپ فاکتور"
            onClick={() => navigate("/order/invoice/" + row.id)}
          />
          {tools.orderStatusIs(row.orderStatusSlug, [Status.Packing]) && (
            <i
              className="packingSvg clickable"
              title="بسته‌بندی"
              onClick={() => navigate("/order/bijack/" + row.id)}
            />
          )}
        </div>
      ),
    },
  ];

  /* ── Data ─────────────────────────────────────────── */
  const fetchData = async () => {
    setTableLoading(true);
    dispatch(setLoading(true));
    const res = await restApi(endpoints.order.index, true).get();
    setData(res.data ?? []);
    dispatch(setLoading(false));
    setTableLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = [...data].filter(tabFilter).sort((a, b) => b.id - a.id);

  /* ── Render ───────────────────────────────────────── */
  return (
    <div className="dashboardBody">
      <Sidebar />
      <div className="dashBoardMain">
        <Header title="سفارش‌ها" />
        <div className="dashBoardInner">
          {/* Tab bar */}
          <div className="dashTabs">
            {Object.entries(tabTitles).map(([key, label]) => (
              <span
                key={key}
                className={`ordersTag ${key === tab ? "activeTab" : ""}`}
                onClick={() => setTab(key)}
              >
                {label}
              </span>
            ))}
          </div>

          {/* Action bar */}
          <div className="searchContainer">
            <div className="backSpan">
              <span
                className="dashboardHeader clickable"
                onClick={() => navigate("/order/add")}
              >
                <p>سفارش جدید</p>
                <i className="addPlus" />
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="dashCard">
            <DataTable
              columns={columns}
              data={filteredData}
              pageSize={15}
              loading={tableLoading}
              rowKey="id"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
